import React, { useState, useEffect, useRef } from 'react';
import styles from './previewScreen.module.scss';
import { CartIcon, DishIcon, HomeIcon } from '../../../../../../../../../Components/IconComponents';
import { getSmartBusinessTitle } from '../../../../../../../../../Costants/companyNameSubtitleKeywords';

interface PreviewScreenProps {
    templateColors: {
        light: Record<string, string>;
        dark: Record<string, string>;
    };
    companyTitle: string;
}

const PreviewScreen: React.FC<PreviewScreenProps> = ({ templateColors, companyTitle }) => {
    const [isDarkMode, setIsDarkMode] = useState(false);
    const [selectedCategory, setSelectedCategory] = useState(1);
    const [selectedTab, setSelectedTab] = useState('menu'); // Add tab state
    const phoneScreenRef = useRef<HTMLDivElement>(null);

    const currentColors = isDarkMode ? templateColors.dark : templateColors.light;

    // Auto-switch theme based on scroll position
    useEffect(() => {
        const handleScroll = () => {
            const lightModeSection = document.querySelector('[data-section="light-mode"]');
            const darkModeSection = document.querySelector('[data-section="dark-mode"]');

            if (lightModeSection && darkModeSection) {
                const lightRect = lightModeSection.getBoundingClientRect();
                const darkRect = darkModeSection.getBoundingClientRect();
                const viewportHeight = window.innerHeight;

                // Check if dark mode section is in view
                if (darkRect.top < viewportHeight && darkRect.bottom > 0) {
                    setIsDarkMode(true);
                }
                // Check if light mode section is in view
                else if (lightRect.top < viewportHeight && lightRect.bottom > 0) {
                    setIsDarkMode(false);
                }
            }
        };

        window.addEventListener('scroll', handleScroll);
        handleScroll(); // Check initial position

        return () => window.removeEventListener('scroll', handleScroll);
    }, []);

    // Apply colors only to the preview screen container
    useEffect(() => {
        const phoneScreen = phoneScreenRef.current;
        if (phoneScreen) {
            Object.entries(currentColors).forEach(([key, value]) => {
                phoneScreen.style.setProperty(key, value);
            });
        }

        // Cleanup function to restore original colors when component unmounts
        return () => {
            if (phoneScreen) {
                // Reset all CSS variables to empty string to remove custom values
                Object.keys(currentColors).forEach((key) => {
                    phoneScreen.style.setProperty(key, '');
                });
            }
        };
    }, [currentColors]);

    const categories = [
        { id: 1, name: "Burgers", image: "🍔" },
        { id: 2, name: "Drinks", image: "🥤" },
        { id: 3, name: "Pizza", image: "🍕" },
        { id: 4, name: "Desserts", image: "🍰" },
        { id: 5, name: "Salads", image: "🥗" }
    ];

    const menuItems = [
        // Burgers (Category 1)
        { id: 1, name: "Classic Burger", price: "15", description: "Medium, No extras", image: "🍔", category: 1 },
        { id: 2, name: "Cheese Burger", price: "17", description: "With extra cheese", image: "🍔", category: 1 },
        { id: 3, name: "Bacon Burger", price: "19", description: "Crispy bacon", image: "🍔", category: 1 },
        { id: 4, name: "Veggie Burger", price: "14", description: "Plant-based patty", image: "🍔", category: 1 },

        // Drinks (Category 2)
        { id: 5, name: "Cola Drink", price: "4", description: "500ml", image: "🥤", category: 2 },
        { id: 6, name: "Orange Juice", price: "5", description: "Fresh squeezed", image: "🥤", category: 2 },
        { id: 7, name: "Coffee", price: "3", description: "Hot or iced", image: "☕", category: 2 },
        { id: 8, name: "Water", price: "2", description: "Bottled", image: "💧", category: 2 },

        // Pizza (Category 3)
        { id: 9, name: "Margherita", price: "18", description: "Medium size", image: "🍕", category: 3 },
        { id: 10, name: "Pepperoni", price: "20", description: "Extra pepperoni", image: "🍕", category: 3 },
        { id: 11, name: "BBQ Chicken", price: "22", description: "Grilled chicken", image: "🍕", category: 3 },
        { id: 12, name: "Veggie", price: "16", description: "Mixed vegetables", image: "🍕", category: 3 },

        // Desserts (Category 4)
        { id: 13, name: "Chocolate Cake", price: "12", description: "Slice", image: "🍰", category: 4 },
        { id: 14, name: "Ice Cream", price: "8", description: "Vanilla scoop", image: "🍦", category: 4 },
        { id: 15, name: "Apple Pie", price: "10", description: "Warm slice", image: "🥧", category: 4 },
        { id: 16, name: "Tiramisu", price: "14", description: "Italian classic", image: "🍰", category: 4 },

        // Salads (Category 5)
        { id: 17, name: "Caesar Salad", price: "11", description: "Fresh romaine", image: "🥗", category: 5 },
        { id: 18, name: "Greek Salad", price: "13", description: "Feta & olives", image: "🥗", category: 5 },
        { id: 19, name: "Chicken Salad", price: "15", description: "Grilled chicken", image: "🥗", category: 5 },
        { id: 20, name: "Garden Salad", price: "9", description: "Mixed greens", image: "🥗", category: 5 }
    ];

    const filteredItems = menuItems.filter(item => item.category === selectedCategory);
    const { hasSmartSubtitle, mainTitle, subtitle } = getSmartBusinessTitle(companyTitle || 'Restaurant Name');

    return (
        <div className={styles.previewContainer}>
            <div className={styles.previewHeader}>
                <h3 className={styles.previewTitle}>Live Preview</h3>
                <div className={styles.themeToggle}>
                    <button
                        className={`${styles.toggleBtn} ${!isDarkMode ? styles.active : ''}`}
                        onClick={() => setIsDarkMode(false)}
                    >
                        Light
                    </button>
                    <button
                        className={`${styles.toggleBtn} ${isDarkMode ? styles.active : ''}`}
                        onClick={() => setIsDarkMode(true)}
                    >
                        Dark
                    </button>
                </div>
            </div>

            <div className={styles.phoneFrame}>
                <div className={styles.phoneScreen} ref={phoneScreenRef}>
                    {/* Header */}
                    <div className={styles.header}>
                        <div className={styles.logoBox}>
                            <div className={styles.logo}>🍽️</div>
                        </div>
                        {
                            hasSmartSubtitle
                                ? (
                                    <div className={styles.smartNameBlock}>
                                        <h1 className={styles.smartMainTitle}>{mainTitle}</h1>
                                        <p className={styles.smartSubtitle}>{subtitle}</p>
                                    </div>
                                )
                                : <h1 className={styles.restaurantName}>{companyTitle || 'Restaurant Name'}</h1>
                        }
                    </div>

                    {/* Category Slider */}
                    <div className={styles.categorySlider}>
                        <div className={styles.sliderContainer}>
                            <div className={styles.slider}>
                                {categories.map(category => (
                                    <div
                                        key={category.id}
                                        className={`${styles.categoryCard} ${selectedCategory === category.id ? styles.selectedCard : ''}`}
                                        onClick={() => setSelectedCategory(category.id)}
                                    >
                                        <div className={styles.cardImage}>
                                            <div className={styles.imagePlaceholder}>{category.image}</div>
                                            {/* <div className={styles.title}>
                                                <p>{category.name}</p>
                                            </div> */}
                                        </div>
                                        {selectedCategory === category.id && (
                                            <div className={styles.selectedBox}>
                                                <p>Current</p>
                                            </div>
                                        )}
                                    </div>
                                ))}
                            </div>
                        </div>
                    </div>

                    {/* Menu Items Grid */}
                    <div className={styles.menuContent}>
                        <div className={styles.cardsBox}>
                            {filteredItems.map(item => (
                                <div key={item.id} className={styles.menuCard}>
                                    <div className={styles.image}>
                                        <div className={styles.imagePlaceholder}>{item.image}</div>
                                    </div>
                                    <div className={styles.itemInfo}>
                                        <div className={styles.price}>
                                            <p>{item.price}</p>
                                            <p>$</p>
                                        </div>
                                        <div className={styles.title}>
                                            <p>{item.name}</p>
                                        </div>
                                        <div className={styles.option}>
                                            <p>{item.description}</p>
                                        </div>
                                    </div>
                                    <button className={styles.addButton}>
                                        <span>+</span>
                                        <span>Add</span>
                                    </button>
                                </div>
                            ))}
                        </div>
                    </div>

                    {/* Footer */}
                    <div className={styles.footer}>
                        <div className={styles.footerNav}>
                            <div
                                className={`${styles.navItem} ${selectedTab === 'home' ? styles.navItemActive : ''}`}
                                onClick={() => setSelectedTab('home')}
                            >
                                {/* <div className={styles.navIcon}>🏠</div> */}
                                <HomeIcon color={selectedTab === 'home' ? 'var(--primary-hover)' : 'var(--input-label)'} />
                                <p>Home</p>
                            </div>
                            <div
                                className={`${styles.navItem} ${selectedTab === 'menu' ? styles.navItemActive : ''}`}
                                onClick={() => setSelectedTab('menu')}
                            >
                                <DishIcon color={selectedTab === 'menu' ? 'var(--primary-hover)' : 'var(--input-label)'} />
                                <p>Menu</p>
                            </div>
                        </div>

                        {/* Floating Cart Button */}
                        <div className={styles.cartButton}>
                            <CartIcon width={15} height={15} color={"white"} />
                        </div>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default PreviewScreen;